import InfoTip from "../../../components/InfoTip";
import { capitalizeFirstLetter } from "../../../components/utils/Date";
import { ReactComponent as HelpIcon } from "../../../static/assets/dashboard-help.svg";
import { StatisticsSegmentedControl } from "./StatisticsPeriodToggle";
import {
  findRanking,
  formatAveragePackmatRate,
} from "../utils/collectionChartUtils";

const RANKING_ARROW = {
  up: "fa-arrow-up",
  down: "fa-arrow-down",
  stable: "fa-arrow-right",
};

const formatRoundedPercent = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${Math.round(Number(value)).toLocaleString()}%`;
};

const getPerformanceStatus = (performance, t) => {
  if (Math.round(performance) >= 100) {
    return {
      label: t("Optimal"),
      icon: "fa-check",
      className: "bg-[#E9FBFB] text-[#008685]",
      tooltip: t("compaction.statusOptimalTooltip"),
    };
  }
  return {
    label: t("Underperforming"),
    icon: "fa-exclamation-triangle",
    className: "bg-[#FEF6E7] text-[#B8860B]",
    tooltip: t("compaction.statusUnderperformingTooltip"),
  };
};

const HeaderCell = ({ label, help }) => (
  <span className="collection-trend-cell collection-trend-cell--help">
    <span>{label}</span>
    <InfoTip icon={HelpIcon} widthMaxRem={18} nudgeY={0}>
      {help}
    </InfoTip>
  </span>
);

const CollectionPerformanceSection = ({
  dataType,
  setDataType,
  displayData,
  trendByWasteCategoryPrevious,
  t,
}) => {
  const isFlux = dataType === "flux";

  return (
    <article className="statistics-card collection-trend">
      <div className="dashboard-chart-heading dashboard-chart-heading-row">
        <div className="dashboard-chart-heading-text">
          <h5 className="dashboard-chart-title">
            {isFlux
              ? t("performanceSummary.byFluxTitle")
              : t("performanceSummary.byContainerTitle")}
          </h5>
          <p className="dashboard-chart-subtitle">
            {t("performanceSummary.subtitle")}
          </p>
        </div>

        <StatisticsSegmentedControl
          value={dataType}
          onChange={setDataType}
          options={[
            { value: "flux", label: t("Flux") },
            { value: "container", label: t("Container") },
          ]}
        />
      </div>

      <div className="collection-performance-scroll">
        <div className="collection-trend-table">
          <div className="collection-trend-row collection-trend-row--head">
            <span className="collection-trend-cell">
              {isFlux ? t("Flux") : t("Container")}
            </span>
            <HeaderCell
              label={t("Rotations")}
              help={t("performanceSummary.help.rotations")}
            />
            <HeaderCell
              label={t("performanceSummary.compactions")}
              help={t("performanceSummary.help.compactions")}
            />
            <HeaderCell
              label={t("performanceSummary.filling")}
              help={t("performanceSummary.help.filling")}
            />
            <HeaderCell
              label={t("performanceSummary.gain")}
              help={t("performanceSummary.help.gain")}
            />
            <HeaderCell
              label={t("performanceSummary.performance")}
              help={t("performanceSummary.help.performance")}
            />
            <HeaderCell
              label={t("Status")}
              help={t("performanceSummary.help.status")}
            />
          </div>

          {displayData.map((item, index) => {
            const performanceStatus = getPerformanceStatus(item.performance, t);
            const ranking = isFlux
              ? findRanking(
                  item.wasteCategory,
                  item.rotations,
                  trendByWasteCategoryPrevious,
                )
              : null;

            return (
              <div key={index} className="collection-trend-row">
                <span className="collection-trend-cell">
                  <span className="block truncate">
                    {isFlux
                      ? item.wasteCategory
                      : capitalizeFirstLetter(item.positioning ?? "")}
                  </span>
                  {!isFlux && item.wasteName ? (
                    <span className="block truncate text-xs text-gray-500">
                      {item.wasteName}
                    </span>
                  ) : null}
                </span>

                <div className="collection-trend-rotations collection-trend-rotations--right">
                  {isFlux ? (
                    <i
                      className={`fas ${RANKING_ARROW[ranking]} collection-trend-rank collection-trend-rank--${ranking}`}
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className="collection-trend-rotations-value">
                    {item.rotations}
                  </span>
                  {isFlux ? (
                    <InfoTip icon={HelpIcon} widthMaxRem={18}>
                      {t("rotation_comparison", {
                        rotations:
                          trendByWasteCategoryPrevious.find(
                            (waste) =>
                              waste.wasteCategory === item.wasteCategory,
                          )?.rotations ?? 0,
                      })}
                    </InfoTip>
                  ) : null}
                </div>

                <span className="collection-trend-cell collection-trend-cell--right">
                  {formatAveragePackmatRate(item.averagePackmatRate)}
                </span>
                <span className="collection-trend-cell collection-trend-cell--right">
                  {formatRoundedPercent(item.averageFillRate)}
                </span>
                <span className="collection-trend-cell collection-trend-cell--right">
                  {formatRoundedPercent(item.averageGain)}
                </span>
                <span className="collection-trend-cell collection-trend-cell--right">
                  {formatRoundedPercent(item.performance)}
                </span>

                <span className="collection-trend-cell flex justify-end">
                  <span
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-semibold ${performanceStatus.className}`}
                    title={performanceStatus.tooltip}
                  >
                    <i className={`fas ${performanceStatus.icon}`} />
                    {performanceStatus.label}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
};

export default CollectionPerformanceSection;
